/**
 * SchemaPickerDialog - Modal dialog for selecting a schema library entry
 *
 * Used when linking a dataset to an existing schema from the schema library.
 * Provides search filtering and displays key schema metadata for each entry.
 */

import { useState, useEffect } from 'react';
import { Loader2, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useSchemas } from '@/features/schemas/hooks/useSchemas';
import { CompatibilityBadge } from './CompatibilityBadge';
import type { SchemaListItem } from '@/features/schemas/types/schema.types';

interface SchemaPickerDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog is closed */
  onClose: () => void;
  /** Callback when a schema is selected */
  onSelect: (schema: SchemaListItem) => void;
}

/**
 * SchemaPickerDialog Component
 *
 * Displays a searchable list of schema library entries for the user to pick from.
 * Shows schema name, field count, compatibility mode, and usage count.
 *
 * @example
 * ```tsx
 * const [pickerOpen, setPickerOpen] = useState(false);
 *
 * <SchemaPickerDialog
 *   open={pickerOpen}
 *   onClose={() => setPickerOpen(false)}
 *   onSelect={(schema) => {
 *     linkSchema(schema.id);
 *     setPickerOpen(false);
 *   }}
 * />
 * ```
 */
export function SchemaPickerDialog({ open, onClose, onSelect }: SchemaPickerDialogProps) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: schemasData, isLoading } = useSchemas({
    size: 100,
    search: debouncedSearch || undefined,
  });

  const filteredSchemas = schemasData?.items ?? [];

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Select Schema</DialogTitle>
          <DialogDescription>
            Choose a schema from the library to link to this dataset.
          </DialogDescription>
        </DialogHeader>

        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search schemas..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Schema list */}
        <div className="flex-1 overflow-y-auto min-h-0 -mx-2 px-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredSchemas.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              {search ? 'No schemas match your search.' : 'No schemas available.'}
            </div>
          ) : (
            <div className="space-y-1">
              {filteredSchemas.map((schema) => (
                <button
                  key={schema.id}
                  type="button"
                  onClick={() => onSelect(schema)}
                  className="w-full text-left p-3 rounded-md border border-transparent hover:border-border hover:bg-accent transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{schema.name}</span>
                    <CompatibilityBadge mode={schema.compatibility_mode} />
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {schema.field_count} fields
                    {schema.usageCount !== undefined && schema.usageCount > 0
                      ? ` \u00B7 ${schema.usageCount} dataset${schema.usageCount !== 1 ? 's' : ''} linked`
                      : ''}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
